import { useCallback } from "react";
import { calculateQuantity, setDependencies, findManufacturerProductId, hasColorOptions } from "./utils/productHelpers";
import { MATERIAL_DEPENDENCIES } from "./utils/materialDependencies";

interface UseMaterialUpdateProps {
    materialSchema: any[];
    measurements: any;
    setSelectedValues: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

/**
 * useMaterialUpdate
 * -----------------
 * This hook handles everything that happens when the USER clicks something.
 * Pure state updates only. Sync is handled separately by the orchestrator.
 */
export function useMaterialUpdate({ materialSchema, measurements, setSelectedValues }: UseMaterialUpdateProps) {
    const apply = useCallback(
        (updater: (prev: Record<string, any>) => Record<string, any>) => {
            setSelectedValues((prev) => updater(prev));
        },
        [setSelectedValues],
    );

    // ─── ACTION 1: User clicks the +/- quantity buttons ──────────────────────
    const handleQuantityUpdate = useCallback(
        (category: string, direction: "add" | "remove") => {
            apply((prev) => {
                const currentQty = prev[category]?.quantity || 0;
                const newQty = direction === "add" ? currentQty + 1 : Math.max(0, currentQty - 1);
                return { ...prev, [category]: { ...prev[category], quantity: newQty } };
            });
        },
        [apply],
    );

    // ─── ACTION 2: User clicks the trash icon to remove a product ────────────
    const handleClearProduct = useCallback(
        (category: string) => {
            apply((prev) => {
                const next = { ...prev };
                const rule = MATERIAL_DEPENDENCIES.find((r) => r.Category === category);
                rule?.RequiresCategories.forEach((dep) => delete next[dep]);
                delete next[category];
                return next;
            });
        },
        [apply],
    );

    // ─── ACTION 3: User picks a product from the dropdown ────────────────────
    const handleProductChange = useCallback(
        (product: any, category: string) => {
            if (!product) return;
            apply((prev) => {
                const next = { ...prev };
                const rule = MATERIAL_DEPENDENCIES.find((r) => r.Category === category);
                rule?.RequiresCategories.forEach((dep) => delete next[dep]);
                next[category] = {
                    value: product,
                    quantity: calculateQuantity(materialSchema, measurements, category, product),
                    manufacturerProductId: findManufacturerProductId(materialSchema, category, product),
                    awaitingColor: hasColorOptions(materialSchema, category, product), // Block sync until color is picked
                };
                setDependencies(MATERIAL_DEPENDENCIES, next, category, product, materialSchema, measurements);
                return next;
            });
        },
        [materialSchema, measurements, apply],
    );

    // ─── ACTION 4: User picks a color from the color dropdown ────────────────
    const handleColorChange = useCallback(
        (category: string, color: string | null, manufacturerId?: string) => {
            apply((prev) => ({
                ...prev,
                [category]: {
                    ...prev[category],
                    color: color || undefined,
                    manufacturerProductId: manufacturerId,
                    awaitingColor: false, // Color picked — sync is now allowed
                },
            }));
        },
        [apply],
    );

    return { handleQuantityUpdate, handleClearProduct, handleProductChange, handleColorChange };
}
